import {loggerFactory} from "@cm/browser-log"
import type {HalImage, HalImageDescriptor, PartialHalImageDescriptor} from "@cm/hal"
import {completeHalImageDescriptor, HalRef, isHalImageDescriptor} from "@cm/hal"
import {bytesToSize, traceObject} from "@cm/utils"
import {deepCopy} from "@cm/utils/misc"
import {WebGl2ImageView} from "#webgl2/webgl2-image-view"
import {WebGl2ImageCacheSizeLookup} from "#webgl2/webgl2-image-cache-size-lookup"
import {OutOfMemoryError} from "#webgl2/webgl2-image-physical"
import type {WebGl2Context} from "#webgl2/webgl2-context"

const ALWAYS_CLEAR_NEW_IMAGES = false // should only be enabled for debugging purposes

export abstract class WebGl2ImageCacheBase {
    protected abstract cacheName: string

    protected abstract estimateImageSizeInBytes(descriptor: HalImageDescriptor): number
    protected abstract allocImage(descriptor: HalImageDescriptor): HalImage

    constructor(
        readonly halContext: WebGl2Context,
        sizeInBytes: number,
        private allowViews: boolean,
    ) {
        this.cacheStats = {
            numAvailableImages: 0,
            numUsedImages: 0,
            usedSizeInBytes: 0,
            maxSizeInBytes: sizeInBytes,
        }
    }

    dispose(): void {
        if (this.usedImages.size > 0) {
            logger.warn("ImageCache is being disposed with used images still in use.")
        }
        this._state = "disposing"
        this.freeAllImages()
        this._state = "disposed"
    }

    get state() {
        return this._state
    }

    get stats(): CacheStats {
        return this.cacheStats
    }

    getImagePtr(partialDescriptor: PartialHalImageDescriptor, options?: {clear?: boolean}): HalRef<HalImage> {
        if (this._state !== "active") {
            throw Error("Image cache has been disposed")
        }
        const image = this.getImage(partialDescriptor, options)
        return new HalRef(image, () => {
            if (this._state === "active") {
                this.releaseImage(image)
            }
        })
    }

    private getImage(partialDescriptor: PartialHalImageDescriptor, options?: {clear?: boolean}): HalImage {
        const maxMarginScale = this.allowViews ? 100000 : 1 // if we allow views, the margin is virtually infinite
        const descriptor = this.getCompleteDescriptor(partialDescriptor)
        const formatHash = this.getFormatHash(descriptor)
        const availableImages = this.availableImagesByFormatHash.get(formatHash)
        let target = availableImages?.findBest(descriptor.width, descriptor.height, maxMarginScale)
        if (target) {
            availableImages?.remove(target)
            this.cacheStats.numAvailableImages--
        } else {
            target = this.createImage(descriptor)
        }
        this.usedImages.add(target)
        this.cacheStats.numUsedImages++
        this.registerImageUsage(target)
        if (this.allowViews) {
            target = target.createView({
                type: "sub-image",
                region: {
                    x: 0,
                    y: 0,
                    width: descriptor.width,
                    height: descriptor.height,
                },
            })
        }
        if (options?.clear || ALWAYS_CLEAR_NEW_IMAGES) {
            target.clear()
        }
        return target
    }

    protected releaseImage(image: HalImage): void {
        if (this.allowViews) {
            while (image instanceof WebGl2ImageView) {
                image = image.image
            }
        }
        if (!this.usedImages.has(image)) {
            throw Error("Image not in use")
        }
        this.usedImages.delete(image)
        const descriptor = this.getCompleteDescriptor(image)
        const formatHash = this.getFormatHash(descriptor)
        let availableImages = this.availableImagesByFormatHash.get(formatHash)
        if (!availableImages) {
            availableImages = new WebGl2ImageCacheSizeLookup<HalImage>()
            this.availableImagesByFormatHash.set(formatHash, availableImages)
        }
        availableImages.add(image)
        this.cacheStats.numUsedImages--
        this.cacheStats.numAvailableImages++
        this.registerImageRelease(image)
    }

    private releaseAllImages(): void {
        this.usedImages.forEach((image) => this.releaseImage(image))
    }

    freeUnusedImages(minBytesToFree: number, optimalBytesToFree: number): void {
        logger.debug(
            `ImageCacheWebGL2(${this.cacheName}): freeing unused images to free at least ${bytesToSize(minBytesToFree)} (optimally ${bytesToSize(optimalBytesToFree)})`,
        )
        const imageAndLastReleased: [HalImage, number][] = []
        this.usageStatsByImage.forEach((usageStats, image) => {
            if (usageStats.isAvailable && usageStats.lastReleased) {
                imageAndLastReleased.push([image, usageStats.lastReleased])
            }
        })
        imageAndLastReleased.sort((a, b) => b[1] - a[1])
        const cacheSizeBefore = this.cacheStats.usedSizeInBytes
        let imagesFreed = 0
        for (;;) {
            const bytesFreed = cacheSizeBefore - this.cacheStats.usedSizeInBytes
            if (bytesFreed >= optimalBytesToFree) {
                break
            }
            if (imageAndLastReleased.length === 0) {
                if (bytesFreed < minBytesToFree) {
                    logger.warn(
                        `ImageCacheWebGL2(${this.cacheName}): Attempting to free ${bytesToSize(minBytesToFree)}, but no more images to free. Proceeding with over-limit allocation.`,
                    )
                }
                break
            }
            const [image] = imageAndLastReleased.pop()!
            this.freeImage(image)
            imagesFreed++
        }
        logger.debug(`ImageCacheWebGL2(${this.cacheName}): freed ${imagesFreed} images (${bytesToSize(cacheSizeBefore - this.cacheStats.usedSizeInBytes)})`)
    }

    private freeAllImages() {
        this.releaseAllImages()
        this.freeAllUnusedImages()
    }

    listImages(allocatedOnly: boolean) {
        for (const [image, usageStats] of this.usageStatsByImage.entries()) {
            if (!allocatedOnly || !usageStats.isAvailable) {
                logger.log(
                    `ImageCacheWebGL2(${this.cacheName}): ${traceObject(image.descriptor)} (${bytesToSize(this.estimateImageSizeInBytes(image.descriptor))} / ${usageStats.isAvailable ? "available" : "in use"})`,
                )
            }
        }
    }

    freeAllUnusedImages() {
        const unusedImages: HalImage[] = []
        this.availableImagesByFormatHash.forEach((images) => unusedImages.push(...images.getAll()))
        this.freeImages(unusedImages)
    }

    private createImage(descriptor: HalImageDescriptor) {
        const id = this.nextId++
        const debugId = `WebGl2ImageCache[${id}]`
        const approximateImageSizeInBytes = this.estimateImageSizeInBytes(descriptor)
        let image: HalImage
        for (;;) {
            if (this.cacheStats.usedSizeInBytes + approximateImageSizeInBytes > this.cacheStats.maxSizeInBytes) {
                // let's free at least the amount of memory we need to allocate the new image OR to 90% of the max cache size
                const memoryToFreeForHeadroom = this.cacheStats.usedSizeInBytes - 0.9 * this.cacheStats.maxSizeInBytes
                const minMemoryToFree = this.cacheStats.usedSizeInBytes + approximateImageSizeInBytes - this.cacheStats.maxSizeInBytes
                const idealMemoryToFree = Math.max(minMemoryToFree, memoryToFreeForHeadroom)
                this.freeUnusedImages(minMemoryToFree, idealMemoryToFree)
            }
            try {
                logger.trace(`ImageCacheWebGL2(${this.cacheName}): allocating image with descriptor ${traceObject(descriptor)} with id ${debugId}`)
                image = this.allocImage(descriptor)
                break
            } catch (e) {
                if (e instanceof OutOfMemoryError) {
                    // we ran out of memory, try to free up some space and try again
                    this.cacheStats.maxSizeInBytes = this.cacheStats.usedSizeInBytes - approximateImageSizeInBytes
                    logger.warn(
                        `ImageCacheWebGL2(${this.cacheName}): Out of memory. Setting max cache size to ${bytesToSize(this.cacheStats.maxSizeInBytes)} bytes and retrying.`,
                    )
                } else {
                    throw e
                }
            }
        }
        this.originalDescriptorByHalImage.set(image, deepCopy(descriptor))
        this.usageStatsByImage.set(image, {isAvailable: true})
        const imageSizeInBytes = this.estimateImageSizeInBytes(image.descriptor) // re-estimate size after creation as it may have changed descriptor due to format constraints
        this.cacheStats.usedSizeInBytes += imageSizeInBytes
        return image
    }

    private freeImage(image: HalImage): void {
        logger.debug(`ImageCacheWebGL2(${this.cacheName}): freeing image with id ${traceObject(image.descriptor)}`)
        const descriptor = this.getCompleteDescriptor(image)
        const formatHash = this.getFormatHash(descriptor)
        const availableImages = this.availableImagesByFormatHash.get(formatHash)
        if (!availableImages) {
            throw Error("availableImages not found")
        }
        availableImages.remove(image)
        this.cacheStats.numAvailableImages--
        this.usageStatsByImage.delete(image)
        const imageSizeInBytes = this.estimateImageSizeInBytes(image.descriptor)
        this.cacheStats.usedSizeInBytes -= imageSizeInBytes
        if (this.cacheStats.usedSizeInBytes < 0) {
            throw Error("Unexpected negative cache size")
        }
        if (!this.originalDescriptorByHalImage.has(image)) {
            throw Error("Original descriptor not found for image")
        }
        this.originalDescriptorByHalImage.delete(image)
        image.dispose()
    }

    private freeImages(images: HalImage[]): void {
        for (const image of images.slice()) {
            this.freeImage(image)
        }
    }

    private registerImageUsage(image: HalImage): void {
        const usage = this.usageStatsByImage.get(image)
        if (!usage) {
            throw Error("usage not found")
        }
        usage.lastReleased = undefined
        usage.isAvailable = false
    }

    private registerImageRelease(image: HalImage): void {
        const usage = this.usageStatsByImage.get(image)
        if (!usage) {
            throw Error("usage not found")
        }
        usage.lastReleased = Date.now()
        usage.isAvailable = true
    }

    private getCompleteDescriptor(descriptorOrImage: PartialHalImageDescriptor | HalImage): HalImageDescriptor {
        const partialDescriptor = isHalImageDescriptor(descriptorOrImage) ? descriptorOrImage : this.originalDescriptorByHalImage.get(descriptorOrImage)
        if (!partialDescriptor) {
            throw Error("Original descriptor not found")
        }
        return completeHalImageDescriptor(partialDescriptor)
    }

    private getFormatHash(descriptor: HalImageDescriptor): string {
        return `${descriptor.numLayers} ${descriptor.channelLayout} ${descriptor.dataType} ${descriptor.numMipLevels} ${descriptor.noRenderTarget ?? false}`
    }

    private readonly cacheStats: CacheStats
    private readonly originalDescriptorByHalImage = new Map<HalImage, HalImageDescriptor>()
    private readonly availableImagesByFormatHash = new Map<string, WebGl2ImageCacheSizeLookup<HalImage>>()
    private readonly usedImages = new Set<HalImage>()
    private readonly usageStatsByImage = new Map<HalImage, ImageUsageStats>()
    private nextId: number = 1
    private _state: CacheState = "active"
}

type CacheStats = {
    numAvailableImages: number
    numUsedImages: number
    usedSizeInBytes: number
    maxSizeInBytes: number
}

type ImageUsageStats = {
    lastReleased?: number
    isAvailable: boolean
}

type CacheState = "active" | "disposing" | "disposed"

const logger = loggerFactory.getLogger("webgl2")
